// components/layout/Navbar.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./Navbar.css";
import Iconify from "../pages/login/Iconify";
import { useNavigate, useLocation } from "react-router-dom";

const MENU_WIDTH = 292;
const NOTIFICATION_WIDTH = 330;

const readLoggedUser = () => {
  try {
    const raw = sessionStorage.getItem("user_token");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const titleCase = (value) => {
  const cleaned = String(value || "")
    .replace(/[._-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim();

  if (!cleaned) return "Home";

  return cleaned
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const getInitials = (name, email) => {
  const value = String(name || email || "User").trim();

  if (!value) return "U";

  if (value.includes("@")) {
    return value.slice(0, 2).toUpperCase();
  }

  const parts = value.split(/\s+/).filter(Boolean);

  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
};

const avatarHue = (value) => {
  let hash = 0;
  const text = String(value || "User");

  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 31 + text.charCodeAt(i)) & 0xffffff;
  }

  return hash % 360;
};

const getFloatingPosition = (anchor, width) => {
  if (!anchor || typeof window === "undefined") {
    return { top: 72, right: 16, width };
  }

  const rect = anchor.getBoundingClientRect();
  const gap = 10;
  const safePadding = 12;
  const panelWidth = Math.max(width, rect.width);
  const right = Math.max(safePadding, window.innerWidth - rect.right);
  let top = rect.bottom + gap;

  // Keep the menu inside the viewport where possible.
  const estimatedHeight = 420;
  if (top + estimatedHeight > window.innerHeight) {
    top = Math.max(safePadding, rect.top - gap - estimatedHeight);
  }

  return {
    top,
    right,
    width: panelWidth,
  };
};

const copyText = async (value) => {
  const text = String(value || "").trim();

  if (!text) return false;

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {}

  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
};


const getStoredThemeMode = () => {
  try {
    return (
      localStorage.getItem("td_theme") ||
      localStorage.getItem("theme") ||
      localStorage.getItem("crm_theme") ||
      localStorage.getItem("app_theme") ||
      ""
    );
  } catch {
    return "";
  }
};

const normalizeThemeMode = (value) => {
  if (value && typeof value === "object") {
    const paletteMode = value?.palette?.mode || value?.mode || value?.theme;
    if (paletteMode) return normalizeThemeMode(paletteMode);
  }

  if (value === true) return "dark";
  if (value === false) return "light";

  const direct = String(value ?? "").trim().toLowerCase();

  if (["dark", "dark-mode", "darkmode", "night", "black", "1", "true"].includes(direct)) {
    return "dark";
  }

  if (["light", "light-mode", "lightmode", "day", "white", "0", "false"].includes(direct)) {
    return "light";
  }

  const stored = String(getStoredThemeMode() || "").trim().toLowerCase();

  if (["dark", "dark-mode", "darkmode", "night", "black", "1", "true"].includes(stored)) {
    return "dark";
  }

  return "light";
};

function FloatingPortal({ children }) {
  if (typeof document === "undefined") return null;
  return createPortal(children, document.body);
}

export default function Navbar({ theme, onToggleTheme }) {
  const navigate = useNavigate();
  const location = useLocation();

  const profileRef = useRef(null);
  const profileMenuRef = useRef(null);
  const notificationRef = useRef(null);
  const notificationMenuRef = useRef(null);

  const [openMenu, setOpenMenu] = useState(false);
  const [openNotifications, setOpenNotifications] = useState(false);
  const [menuPosition, setMenuPosition] = useState(() => getFloatingPosition(null, MENU_WIDTH));
  const [notificationPosition, setNotificationPosition] = useState(() => getFloatingPosition(null, NOTIFICATION_WIDTH));
  const [toast, setToast] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState(() => {
    if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
    return Notification.permission;
  });
  const [internalTheme, setInternalTheme] = useState(() => normalizeThemeMode(theme));

  const loggedUser = useMemo(readLoggedUser, []);

  const userName =
    loggedUser?.userName ||
    loggedUser?.name ||
    loggedUser?.fullName ||
    loggedUser?.firstName ||
    "User";

  const userEmail =
    loggedUser?.userEmail ||
    loggedUser?.email ||
    loggedUser?.mail ||
    "";

  const userRole =
    loggedUser?.role ||
    loggedUser?.userRole ||
    loggedUser?.accessRole ||
    loggedUser?.accountType ||
    "User";

  const isDark = internalTheme === "dark";
  const initials = getInitials(userName, userEmail);
  const hue = avatarHue(userEmail || userName);

  const breadcrumbs = useMemo(() => {
    const segments = location.pathname.split("/").filter(Boolean);

    if (!segments.length) {
      return [{ label: "Home", path: "/", active: true }];
    }

    return [
      { label: "Home", path: "/", active: false },
      ...segments.map((segment, index) => ({
        label: titleCase(segment),
        path: `/${segments.slice(0, index + 1).join("/")}`,
        active: index === segments.length - 1,
      })),
    ];
  }, [location.pathname]);

  const activeModule = breadcrumbs[breadcrumbs.length - 1]?.label || "Home";

  const showToast = useCallback((message, type = "info") => {
    setToast({ message, type });
  }, []);

  useEffect(() => {
    setInternalTheme(normalizeThemeMode(theme));
  }, [theme]);

  useEffect(() => {
    try {
      localStorage.setItem("td_theme", internalTheme);
      localStorage.setItem("crm_theme", internalTheme);
    } catch {}

    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("data-theme", internalTheme);
      document.body?.setAttribute("data-theme", internalTheme);
      document.body?.classList.toggle("crm-dark-mode", internalTheme === "dark");
    }
  }, [internalTheme]);

  useEffect(() => {
    if (!toast) return undefined;

    const timer = window.setTimeout(() => setToast(null), 2400);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const closeFloatingMenus = useCallback(() => {
    setOpenMenu(false);
    setOpenNotifications(false);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("auth");
    sessionStorage.removeItem("user_token");
    closeFloatingMenus();
    navigate("/");
    window.location.reload();
  }, [closeFloatingMenus, navigate]);

  const toggleProfileMenu = () => {
    setOpenNotifications(false);

    if (openMenu) {
      setOpenMenu(false);
      return;
    }

    setMenuPosition(getFloatingPosition(profileRef.current, MENU_WIDTH));
    setOpenMenu(true);
  };

  const toggleNotifications = () => {
    setOpenMenu(false);

    if (openNotifications) {
      setOpenNotifications(false);
      return;
    }

    setNotificationPosition(getFloatingPosition(notificationRef.current, NOTIFICATION_WIDTH));
    setOpenNotifications(true);
  };

  const refreshFloatingPositions = useCallback(() => {
    if (openMenu) {
      setMenuPosition(getFloatingPosition(profileRef.current, MENU_WIDTH));
    }

    if (openNotifications) {
      setNotificationPosition(getFloatingPosition(notificationRef.current, NOTIFICATION_WIDTH));
    }
  }, [openMenu, openNotifications]);

  useEffect(() => {
    if (!openMenu && !openNotifications) return undefined;

    const onMouseDown = (event) => {
      const target = event.target;

      if (
        profileRef.current?.contains(target) ||
        profileMenuRef.current?.contains(target) ||
        notificationRef.current?.contains(target) ||
        notificationMenuRef.current?.contains(target)
      ) {
        return;
      }

      closeFloatingMenus();
    };

    const onKeyDown = (event) => {
      if (event.key === "Escape") closeFloatingMenus();
    };

    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", refreshFloatingPositions);
    window.addEventListener("scroll", refreshFloatingPositions, true);

    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", refreshFloatingPositions);
      window.removeEventListener("scroll", refreshFloatingPositions, true);
    };
  }, [closeFloatingMenus, openMenu, openNotifications, refreshFloatingPositions]);

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  const handleToggleTheme = () => {
    const nextTheme = isDark ? "light" : "dark";

    // Update locally first, so the navbar changes immediately even if the
    // parent component does not update the `theme` prop.
    setInternalTheme(nextTheme);

    try {
      localStorage.setItem("td_theme", nextTheme);
      localStorage.setItem("crm_theme", nextTheme);
      window.dispatchEvent(new CustomEvent("crm-theme-change", { detail: { theme: nextTheme } }));
    } catch {}

    if (typeof onToggleTheme === "function") {
      onToggleTheme(nextTheme);
    }

    showToast(nextTheme === "dark" ? "Dark mode enabled" : "Light mode enabled", "success");
  };

  const handleToggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen?.();
      } else {
        await document.exitFullscreen?.();
      }
    } catch {
      showToast("Fullscreen is not available in this browser.", "warning");
    }
  };

  const handleCopyEmail = async () => {
    const ok = await copyText(userEmail || userName);
    showToast(ok ? "User details copied" : "Could not copy user details", ok ? "success" : "error");
  };

  const handleCopyCurrentPath = async () => {
    const fullPath = `${window.location.origin}${location.pathname}${location.search || ""}`;
    const ok = await copyText(fullPath);
    showToast(ok ? "Current page link copied" : "Could not copy page link", ok ? "success" : "error");
  };

  const enableNotifications = async () => {
    try {
      if (!("Notification" in window)) {
        setNotificationPermission("unsupported");
        showToast("Browser notifications are not supported.", "warning");
        return;
      }

      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);

      if (permission === "granted") {
        showToast("Browser notifications enabled", "success");
      } else {
        showToast("Browser notifications are blocked", "warning");
      }
    } catch {
      showToast("Could not enable notifications", "error");
    }
  };

  return (
    <>
      <header className={`crm-navbar ${isDark ? "navbar-dark" : ""}`} data-theme={internalTheme}>
        {/* LEFT: Brand + Breadcrumb */}
        <div className="navbar-left">
          <div className="brand-row">
            <div className="brand-mark" aria-hidden="true">
              <Iconify icon="mdi:hexagon-multiple" width={18} />
            </div>
            <div className="crm-title">RingOi CRM</div>
          </div>

          <nav className="breadcrumb" aria-label="Breadcrumb">
            {breadcrumbs.map((item, index) => (
              <React.Fragment key={item.path || item.label}>
                {index > 0 && <span className="breadcrumb-separator">/</span>}
                <span className={item.active ? "breadcrumb-current" : "breadcrumb-item"}>
                  {item.label}
                </span>
              </React.Fragment>
            ))}
          </nav>
        </div>

        {/* CENTER: Current module pill */}
        <div className="navbar-center" title={`Current module: ${activeModule}`}>
          <span className="module-pill-icon">
            <Iconify icon="mdi:view-dashboard-outline" width={16} />
          </span>
          <span className="module-pill-text">{activeModule}</span>
        </div>

        {/* RIGHT: Quick actions + Profile */}
        <div className="navbar-right">
          <button
            type="button"
            className="nav-icon"
            onClick={handleToggleTheme}
            title={isDark ? "Switch to light mode" : "Switch to dark mode"}
            aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
          >
            <Iconify icon={isDark ? "mdi:white-balance-sunny" : "mdi:weather-night"} width={20} />
          </button>

          <button
            type="button"
            className="nav-icon nav-icon-hide-sm"
            onClick={handleToggleFullscreen}
            title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          >
            <Iconify icon={isFullscreen ? "mdi:arrow-collapse" : "mdi:arrow-expand"} width={20} />
          </button>

          <button
            type="button"
            ref={notificationRef}
            className={`nav-icon nav-icon-notification ${openNotifications ? "active" : ""}`}
            onClick={toggleNotifications}
            title="Notifications"
            aria-label="Open notifications"
            aria-expanded={openNotifications}
          >
            <Iconify icon="mdi:bell-outline" width={20} />
            <span className="notification-dot" aria-hidden="true" />
          </button>

          <button
            type="button"
            ref={profileRef}
            className={`profile-box ${openMenu ? "active" : ""}`}
            onClick={toggleProfileMenu}
            aria-haspopup="menu"
            aria-expanded={openMenu}
            title={userEmail || userName}
          >
            <div className="avatar-circle" style={{ "--avatar-hue": hue }}>
              {initials}
            </div>
            <span className="username">{userName}</span>
            <span className="profile-role-chip">{userRole}</span>
            <span className={`profile-chevron ${openMenu ? "open" : ""}`}>
              <Iconify icon="mdi:chevron-down" width={18} />
            </span>
          </button>
        </div>
      </header>

      {toast && (
        <div className={`nav-toast nav-toast-${toast.type} ${isDark ? "navbar-dark" : ""}`} data-theme={internalTheme} role="status">
          <span className="nav-toast-dot" />
          <span>{toast.message}</span>
        </div>
      )}

      {openNotifications && (
        <FloatingPortal>
          <div className="nav-floating-backdrop" aria-hidden="true" />
          <div
            ref={notificationMenuRef}
            className={`nav-floating-panel notification-panel ${isDark ? "navbar-dark" : ""}`}
            data-theme={internalTheme}
            style={{
              top: `${notificationPosition.top}px`,
              right: `${notificationPosition.right}px`,
              width: `${notificationPosition.width}px`,
            }}
            role="dialog"
            aria-label="Notifications"
          >
            <div className="notification-head">
              <div>
                <div className="notification-title">Notifications</div>
                <div className="notification-subtitle">System alerts and browser notifications</div>
              </div>
              <span className={`permission-pill permission-${notificationPermission}`}>
                {notificationPermission === "granted"
                  ? "Enabled"
                  : notificationPermission === "denied"
                  ? "Blocked"
                  : notificationPermission === "unsupported"
                  ? "Unsupported"
                  : "Off"}
              </span>
            </div>

            <div className="notification-empty">
              <div className="notification-empty-icon">
                <Iconify icon="mdi:bell-check-outline" width={24} />
              </div>
              <div>
                <div className="notification-empty-title">No new alerts</div>
                <div className="notification-empty-text">
                  You are viewing <b>{activeModule}</b>. Ticket alerts can be connected here later without changing the layout.
                </div>
              </div>
            </div>

            <div className="dropdown-divider" />

            <button
              type="button"
              className="dropdown-item dropdown-item-button"
              onClick={enableNotifications}
              disabled={notificationPermission === "granted" || notificationPermission === "unsupported"}
            >
              <Iconify icon="mdi:bell-ring-outline" width={18} />
              <span>Enable browser notifications</span>
            </button>

            <button
              type="button"
              className="dropdown-item dropdown-item-button"
              onClick={() => window.location.reload()}
            >
              <Iconify icon="mdi:refresh" width={18} />
              <span>Refresh current module</span>
            </button>
          </div>
        </FloatingPortal>
      )}

      {openMenu && (
        <FloatingPortal>
          <div className="nav-floating-backdrop" aria-hidden="true" />
          <div
            ref={profileMenuRef}
            className={`nav-floating-panel dropdown-menu profile-dropdown ${isDark ? "navbar-dark" : ""}`}
            data-theme={internalTheme}
            style={{
              top: `${menuPosition.top}px`,
              right: `${menuPosition.right}px`,
              width: `${menuPosition.width}px`,
            }}
            role="menu"
            aria-label="User menu"
          >
            <div className="dropdown-user-card">
              <div className="avatar-circle avatar-circle-large" style={{ "--avatar-hue": hue }}>
                {initials}
              </div>
              <div className="dropdown-user-info">
                <div className="dropdown-user-name">{userName}</div>
                <div className="dropdown-user-email">{userEmail || "No email available"}</div>
                <span className="dropdown-user-role">{userRole}</span>
              </div>
            </div>

            <div className="dropdown-divider" />

            <button type="button" className="dropdown-item dropdown-item-button" onClick={handleCopyEmail} role="menuitem">
              <Iconify icon="mdi:content-copy" width={18} />
              <span>Copy user details</span>
            </button>

            <button type="button" className="dropdown-item dropdown-item-button" onClick={handleToggleTheme} role="menuitem">
              <Iconify icon={isDark ? "mdi:white-balance-sunny" : "mdi:weather-night"} width={18} />
              <span>{isDark ? "Switch to light mode" : "Switch to dark mode"}</span>
            </button>

            <button type="button" className="dropdown-item dropdown-item-button" onClick={handleCopyCurrentPath} role="menuitem">
              <Iconify icon="mdi:link-variant" width={18} />
              <span>Copy current page link</span>
            </button>

            <div className="dropdown-session-box">
              <span className="session-label">Current module</span>
              <span className="session-value">{activeModule}</span>
            </div>

            <div className="dropdown-divider" />

            <button type="button" className="dropdown-item dropdown-item-button logout" onClick={logout} role="menuitem">
              <Iconify icon="mdi:logout" width={18} />
              <span>Logout</span>
            </button>
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
